import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getCreditosAdminControllerExtratoQueryKey,
  useCreditosAdminControllerAjustar,
  useCreditosAdminControllerCreditar,
  useCreditosAdminControllerExtrato,
} from '@/api/generated/admin-créditos-de-discussão/admin-créditos-de-discussão';
import type { UsuarioResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

/** Bate com `TETO_POR_LANCAMENTO_DE_CREDITO` do backend. */
const TETO = 1_000;

const ROTULO_TIPO: Record<string, string> = {
  CREDITO: 'Crédito',
  CONSUMO: 'Consumo',
  ESTORNO: 'Estorno',
  AJUSTE: 'Ajuste',
};

const schema = z.object({
  quantidade: z.coerce
    .number()
    .int('Informe um número inteiro')
    .min(-TETO)
    .max(TETO)
    .refine((v) => v !== 0, 'A quantidade não pode ser zero'),
  descricao: z.string().trim().min(3, 'Explique o motivo — é o que fica no extrato').max(500),
});

type Formulario = z.input<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  usuario: UsuarioResponse;
}

/**
 * Créditos de discussão de um usuário: saldo, lançamento e extrato numa tela só.
 *
 * 1 crédito = 1 discussão técnica aberta sem cobrança. Enquanto houver saldo, o
 * médico envia a solicitação e cai direto na discussão — a etapa de pagamento
 * não chega a aparecer para ele.
 *
 * ⚠️ O saldo é DERIVADO do ledger: não existe campo editável em lugar nenhum
 * desta tela. Toda mudança passa por um lançamento com motivo, e o motivo não é
 * burocracia — o ledger é append-only, o lançamento nunca vai poder ser editado
 * nem apagado, e daqui a seis meses a única resposta para "por que esta pessoa
 * ganhou 50 discussões?" é este texto.
 *
 * Quantidade negativa vira AJUSTE, positiva vira CRÉDITO — um campo só em vez
 * de dois botões: o servidor recusa crédito negativo (422), então oferecer os
 * dois caminhos separados só criaria uma forma a mais de errar.
 */
export function CreditosDoUsuarioDialogo({ aberto, aoFechar, usuario }: Props) {
  const queryClient = useQueryClient();
  const { pode } = usePermissoes();
  const podeCreditar = pode('admin_creditos.creditar');

  const [pagina, setPagina] = useState(1);

  const { data, isPending, isError, refetch } = useCreditosAdminControllerExtrato(
    usuario.id,
    { pagina, porPagina: 5, ordenarPor: 'criadoEm', ordem: 'desc' },
    { query: { enabled: aberto } },
  );

  const creditar = useCreditosAdminControllerCreditar();
  const ajustar = useCreditosAdminControllerAjustar();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (aberto) {
      reset({ quantidade: '', descricao: '' });
      setPagina(1);
    }
  }, [aberto, reset]);

  const saldo = data?.usuario.saldo ?? 0;
  const quantidade = Number(watch('quantidade'));
  const previsao = Number.isFinite(quantidade) ? saldo + quantidade : saldo;

  const aoEnviar = handleSubmit(async (bruto) => {
    const dados = schema.parse(bruto);
    const descontando = dados.quantidade < 0;

    try {
      const variaveis = { profileId: usuario.id, data: dados };
      if (descontando) await ajustar.mutateAsync(variaveis);
      else await creditar.mutateAsync(variaveis);

      await queryClient.invalidateQueries({
        queryKey: getCreditosAdminControllerExtratoQueryKey(usuario.id),
      });

      toast.success(descontando ? 'Ajuste lançado.' : 'Créditos lançados.');
      reset({ quantidade: '', descricao: '' });
      setPagina(1);
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível lançar.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Créditos de discussão — {usuario.nomeCompleto}</DialogTitle>
          <DialogDescription>
            Cada crédito vale uma discussão técnica aberta sem cobrança. Os lançamentos entram no
            extrato e não podem ser editados nem apagados depois.
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <EstadoCarregando rotulo="Carregando créditos..." />
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : (
          <div className="space-y-5">
            <div className="rounded-lg bg-muted/50 px-4 py-3 flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Saldo atual</span>
              <span className="text-brand font-semibold text-base">
                {saldo} {saldo === 1 ? 'discussão' : 'discussões'}
              </span>
            </div>

            {podeCreditar && (
              <form onSubmit={aoEnviar} className="space-y-4" noValidate>
                {errors.root?.message && (
                  <Alert variant="destructive">
                    <AlertDescription>{errors.root.message}</AlertDescription>
                  </Alert>
                )}

                <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
                  <Campo
                    id="quantidade"
                    rotulo="Quantidade"
                    erro={errors.quantidade?.message}
                    obrigatorio
                    dica="Negativo desconta."
                  >
                    <Input
                      id="quantidade"
                      type="number"
                      inputMode="numeric"
                      className={CLASSE_CONTROLE}
                      {...register('quantidade')}
                    />
                  </Campo>

                  <Campo id="descricao" rotulo="Motivo" erro={errors.descricao?.message} obrigatorio>
                    <Textarea
                      id="descricao"
                      rows={2}
                      className={CLASSE_CONTROLE}
                      placeholder="Ex.: cortesia de boas-vindas"
                      {...register('descricao')}
                    />
                  </Campo>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    Depois do lançamento:{' '}
                    <span
                      className={previsao < 0 ? 'text-destructive font-medium' : 'text-brand font-medium'}
                    >
                      {previsao}
                    </span>
                    {previsao < 0 && (
                      <span className="block text-xs text-destructive">
                        O servidor recusa: o saldo não pode ficar negativo.
                      </span>
                    )}
                  </p>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Lançar
                  </Button>
                </div>
              </form>
            )}

            {data.itens.length === 0 ? (
              <EstadoVazio
                titulo="Nenhum lançamento"
                descricao="Este usuário nunca movimentou créditos."
              />
            ) : (
              <>
                <div className="overflow-x-auto max-h-72">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Quando</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-right">Quantidade</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                        <TableHead>Motivo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.itens.map((transacao) => (
                        <TableRow key={transacao.id}>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {new Date(transacao.criadoEm).toLocaleDateString('pt-BR')}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {ROTULO_TIPO[transacao.tipo] ?? transacao.tipo}
                            </Badge>
                          </TableCell>
                          <TableCell
                            className={`text-right font-medium ${
                              transacao.quantidade < 0 ? 'text-destructive' : 'text-emerald-600'
                            }`}
                          >
                            {transacao.quantidade > 0 ? '+' : ''}
                            {transacao.quantidade}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {transacao.saldoApos}
                          </TableCell>
                          <TableCell>
                            <span className="text-foreground">{transacao.descricao ?? '—'}</span>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {/* Sem autor = debitado/devolvido pelo sistema na
                                  abertura ou no encerramento da discussão. */}
                              {transacao.criadoPorNome ? `por ${transacao.criadoPorNome}` : 'automático'}
                            </p>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <Paginacao
                  pagina={data.pagina}
                  totalPaginas={data.totalPaginas}
                  total={data.total}
                  porPagina={data.porPagina}
                  aoMudarPagina={setPagina}
                />
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
